<script>
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EvolutionAPI from 'dashboard/api/evolution';
import InboxesAPI from 'dashboard/api/inboxes';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  components: {
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isDisconnecting: false,
      isGeneratingQR: false,
      isRecreating: false,
      qrCode: null,
      instanceStatus: null,
    };
  },
  computed: {
    canShowQRCode() {
      return !this.isStatusOpen(this.instanceStatus);
    },
    isConnected() {
      return this.isStatusOpen(this.instanceStatus);
    },
    statusText() {
      if (this.isConnected)
        return this.$t('INBOX_MGMT.ADD.EVOLUTION.STATUS.CONNECTED');
      if (this.instanceStatus === 'connecting')
        return this.$t('INBOX_MGMT.ADD.EVOLUTION.STATUS.CONNECTING');
      return this.$t('INBOX_MGMT.ADD.EVOLUTION.STATUS.DISCONNECTED');
    },
  },
  watch: {
    instanceStatus(newStatus) {
      if (this.isStatusOpen(newStatus)) {
        this.qrCode = null;
      }
    },
  },
  created() {
    this.checkInstanceStatus();
    emitter.on(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, this.onQrCodeUpdated);
    emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
  },

  unmounted() {
    emitter.off(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, this.onQrCodeUpdated);
    emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
  },
  methods: {
    isStatusOpen(status) {
      return ['open', 'connected'].includes((status || '').toLowerCase());
    },
    onQrCodeUpdated(payload) {
      if (payload.inbox_id !== this.inbox.id) return;
      this.qrCode = payload.qr_code;
    },

    async onConnectionUpdate(payload) {
      if (payload.inbox_id !== this.inbox.id) return;
      this.instanceStatus = payload.status;
      if (this.isStatusOpen(this.instanceStatus)) {
        await this.checkInstanceStatus();
        this.qrCode = null;
      }
    },

    async disconnectInstance() {
      try {
        this.isDisconnecting = true;

        const config = this.inbox?.provider_config || {};
        if (!config.api_url || !config.admin_token || !config.instance_name) {
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.ERROR'));
          return;
        }

        await EvolutionAPI.logout(
          config.api_url,
          config.admin_token,
          config.instance_name
        );

        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.SUCCESS'));
        this.instanceStatus = 'close';
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.ERROR')
        );
      } finally {
        this.isDisconnecting = false;
      }
    },

    async checkInstanceStatus() {
      try {
        const config = this.inbox?.provider_config || {};
        if (!config.api_url || !config.admin_token) {
          this.instanceStatus = 'close';
          return;
        }

        const instances = await EvolutionAPI.fetchInstances(
          config.api_url,
          config.admin_token
        );
        const instance = instances.find(inst => {
          return (
            inst.name === config.instance_name ||
            inst.instanceName === config.instance_name
          );
        });

        if (instance) {
          this.instanceStatus =
            instance.connectionStatus || instance.state || instance.status;

          // Clear QR Code when instance is connected
          if (this.isStatusOpen(this.instanceStatus)) {
            this.qrCode = null;
          }
        } else {
          this.instanceStatus = 'close';
        }
      } catch (error) {
        // Default to closed state if status cannot be fetched
        this.instanceStatus = 'close';
      }
    },

    async generateQRCode() {
      try {
        this.isGeneratingQR = true;

        const config = this.inbox?.provider_config || {};
        if (!config.api_url || !config.admin_token || !config.instance_name) {
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.ERROR'));
          return;
        }

        const response = await EvolutionAPI.getQRCode(
          config.api_url,
          config.admin_token,
          config.instance_name
        );

        // Try different possible response structures - prioritize base64 images
        this.qrCode =
          response.base64 ||
          response.qrcode?.base64 ||
          response.qrCode ||
          response.qr ||
          response.qrcode?.code ||
          response.code ||
          response;

        if (!this.qrCode) {
          useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.NO_QR'));
        }
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.ERROR')
        );
      } finally {
        this.isGeneratingQR = false;
      }
    },

    async recreateInstance() {
      try {
        this.isRecreating = true;
        const response = await InboxesAPI.recreateEvolutionInstance(
          this.inbox.id
        );
        this.qrCode =
          response.data.qrcode?.base64 ||
          response.data.qrcode?.qrCode ||
          response.data.qrcode?.code ||
          response.data.qrcode;
        // QR code will be shown inline, no modal needed
        await this.checkInstanceStatus();
        useAlert(this.$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.SUCCESS'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.ERROR')
        );
      } finally {
        this.isRecreating = false;
      }
    },

    async refreshStatus() {
      await this.checkInstanceStatus();
    },
  },
};
</script>

<template>
  <div class="flex flex-col items-center justify-center py-8">
    <!-- Connection Status -->
    <div class="flex flex-col items-center gap-4 mb-6">
      <div
        class="flex items-center gap-2 px-4 py-2 rounded-full"
        :class="{
          'bg-green-100 dark:bg-green-900/30': isConnected,
          'bg-yellow-100 dark:bg-yellow-900/30':
            instanceStatus === 'connecting',
          'bg-red-100 dark:bg-red-900/30':
            !isConnected && instanceStatus !== 'connecting',
        }"
      >
        <span
          class="w-3 h-3 rounded-full"
          :class="{
            'bg-green-500': isConnected,
            'bg-yellow-500 animate-pulse': instanceStatus === 'connecting',
            'bg-red-500': !isConnected && instanceStatus !== 'connecting',
          }"
        />
        <span
          class="font-medium"
          :class="{
            'text-green-700 dark:text-green-300': isConnected,
            'text-yellow-700 dark:text-yellow-300':
              instanceStatus === 'connecting',
            'text-red-700 dark:text-red-300':
              !isConnected && instanceStatus !== 'connecting',
          }"
        >
          {{ statusText }}
        </span>
      </div>
    </div>

    <!-- QR Code Display (when not connected) -->
    <div v-if="!isConnected" class="flex flex-col items-center gap-4">
      <!-- QR Code Image -->
      <div
        v-if="qrCode"
        class="p-4 bg-white rounded-xl shadow-lg border border-slate-200 dark:border-slate-700"
      >
        <img
          :src="qrCode"
          alt="WhatsApp QR Code"
          class="w-64 h-64 object-contain"
        />
      </div>

      <!-- Placeholder when no QR -->
      <div
        v-else
        class="w-64 h-64 flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600"
      >
        <fluent-icon icon="search" size="48" class="text-slate-400 mb-2" />
        <p class="text-sm text-slate-500 dark:text-slate-400 text-center px-4">
          {{ $t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.CLICK_TO_GENERATE') }}
        </p>
      </div>

      <!-- Instructions -->
      <p
        class="text-sm text-slate-600 dark:text-slate-400 text-center max-w-xs"
      >
        {{ $t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.INSTRUCTIONS') }}
      </p>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-2">
        <NextButton
          :label="$t('INBOX_MGMT.ADD.EVOLUTION.QR_CODE.GENERATE')"
          :is-loading="isGeneratingQR"
          @click="generateQRCode"
        />
        <NextButton
          variant="faded"
          :label="$t('INBOX_MGMT.ADD.EVOLUTION.RECREATE.BUTTON')"
          :is-loading="isRecreating"
          @click="recreateInstance"
        />
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="flex flex-col items-center gap-4">
      <div
        class="w-64 h-64 flex flex-col items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-200 dark:border-green-800"
      >
        <fluent-icon
          icon="checkmark-circle"
          size="64"
          class="text-green-500 mb-2"
        />
        <p class="text-lg font-medium text-green-700 dark:text-green-300">
          {{ $t('INBOX_MGMT.ADD.EVOLUTION.STATUS.CONNECTED') }}
        </p>
        <p
          class="text-sm text-green-600 dark:text-green-400 text-center px-4 mt-1"
        >
          {{ $t('INBOX_MGMT.ADD.EVOLUTION.STATUS.READY') }}
        </p>
      </div>

      <!-- Disconnect Button -->
      <NextButton
        variant="faded"
        color="ruby"
        :label="$t('INBOX_MGMT.ADD.EVOLUTION.DISCONNECT.BUTTON')"
        :is-loading="isDisconnecting"
        @click="disconnectInstance"
      />
    </div>
  </div>
</template>
